import React, { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { isInIframe } from '@/helper/utils'
import { ExternalLink } from 'lucide-react'
import useTheme from '@/contexts/useTheme'
import { getFallbackPrivacyPolicyDocumentUrl } from '@/types'
import { useRouter } from 'next/router'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

const CookieBanner: React.FC = () => {
  const { t } = useTranslation('common')
  const { company } = useTheme()
  const [showBanner, setShowBanner] = useState(false)
  const [showCustomize, setShowCustomize] = useState(false)
  const router = useRouter()
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  const privacyPolicyUrl =
    company?.privacyPolicyDocument ||
    getFallbackPrivacyPolicyDocumentUrl(router.locale ?? 'en')

  useEffect(() => {
    if (isInIframe()) {
      return
    }

    const cookieConsent = localStorage.getItem('cookieConsent')
    if (!cookieConsent) {
      setShowBanner(true)
    }
  }, [])

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    }
    savePreferences(allAccepted)
    setShowBanner(false)
  }

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    }
    savePreferences(onlyNecessary)
    setShowBanner(false)
  }

  const handleCustomize = () => {
    setShowCustomize(true)
  }

  const handleSaveSettings = () => {
    savePreferences(preferences)
    setShowBanner(false)
    setShowCustomize(false)
  }

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs))
    localStorage.setItem('cookieConsentDate', new Date().toISOString())
  }

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return

    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  if (!showBanner) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 md:bottom-4 md:left-auto md:right-4 z-50 px-4 md:px-0">
      <div className="bg-white rounded-lg md:rounded-lg shadow-xl w-full max-w-full md:w-[500px] md:max-w-[500px] mx-auto">
        <div className="p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3">
            {t('cookie_banner_title')}
          </h2>

          {!showCustomize ? (
            <>
              <div className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                <p className="mb-2">{t('cookie_banner_description')}</p>
                <a
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline inline-flex items-center gap-1"
                >
                  {t('cookie_banner_privacy_policy_link')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2 text-sm md:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('cookie_banner_reject_all')}
                </button>
                <button
                  onClick={handleCustomize}
                  className="flex-1 px-4 py-2 text-sm md:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('cookie_banner_customize')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2 text-sm md:text-base bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {t('cookie_banner_accept_all')}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                {t('cookie_banner_customize_description')}
              </p>

              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-medium text-gray-800">
                      {t('cookie_banner_necessary_title')}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600">
                      {t('cookie_banner_necessary_description')}
                    </p>
                  </div>
                  <div className="w-12 h-6 bg-gray-300 rounded-full relative flex-shrink-0">
                    <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 right-0.5 shadow-sm"></div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-medium text-gray-800">
                      {t('cookie_banner_analytics_title')}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600">
                      {t('cookie_banner_analytics_description')}
                    </p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('analytics')}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${
                      preferences.analytics ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                        preferences.analytics
                          ? 'translate-x-6'
                          : 'translate-x-0.5'
                      } shadow-sm`}
                    ></div>
                  </button>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-medium text-gray-800">
                      {t('cookie_banner_marketing_title')}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600">
                      {t('cookie_banner_marketing_description')}
                    </p>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('marketing')}
                    className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${
                      preferences.marketing ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                        preferences.marketing
                          ? 'translate-x-6'
                          : 'translate-x-0.5'
                      } shadow-sm`}
                    ></div>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2 text-sm md:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('cookie_banner_reject_all')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2 text-sm md:text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('cookie_banner_accept_all')}
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="flex-1 px-4 py-2 text-sm md:text-base bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {t('cookie_banner_save_settings')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CookieBanner
